import { useTranslation } from "react-i18next";
import type { Translate } from "../../i18n/translate";
import { errorText } from "../../utils/apiError";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageHeader } from "../../components/PageHeader";
import { showcaseApi, type ShowcaseConfig } from "../../http/showcase";
import { formatTimecode, type ShowcaseVideo } from "@shared/showcase";
import { startUpload, useUploads, type UploadState } from "./uploadManager";

/**
 * Video: Nenda's own sales videos on video.nenda.com, replacing Vimeo
 * (docs/design/video-nenda.md). Nenda only (`sales-videos`).
 */

export function statusChip(
  t: Translate,
  v: ShowcaseVideo,
  upload?: UploadState
) {
  if (upload && upload.phase !== "done" && upload.phase !== "failed") {
    const label =
      upload.phase === "measuring"
        ? t("users:showcasePage.status.preparing")
        : upload.phase === "finishing"
          ? t("users:showcasePage.status.finishing")
          : t("users:showcasePage.status.uploadingPercent", {
              percent: Math.round(upload.progress * 100),
            });
    return <Chip size="small" color="info" label={label} />;
  }
  if (upload?.phase === "failed")
    return (
      <Chip
        size="small"
        color="error"
        label={t("users:showcasePage.status.uploadFailed")}
      />
    );
  if (v.status === "failed")
    return (
      <Chip
        size="small"
        color="error"
        label={t("users:showcasePage.status.failed")}
      />
    );
  if (v.status === "transcoding")
    return (
      <Chip
        size="small"
        color="info"
        label={t("users:showcasePage.status.processing")}
      />
    );
  if (v.status === "awaiting_upload")
    return (
      <Chip size="small" label={t("users:showcasePage.status.noVideoYet")} />
    );
  return v.published ? (
    <Chip
      size="small"
      color="success"
      label={t("users:showcasePage.status.live")}
    />
  ) : (
    <Chip
      size="small"
      variant="outlined"
      label={t("users:showcasePage.status.draft")}
    />
  );
}

function titleFromFile(name: string): string {
  return name
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function NewVideoDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (v: ShowcaseVideo, file: File) => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setTitle("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const pick = (f: File | undefined) => {
    if (!f) return;
    if (f.type && !f.type.startsWith("video/")) {
      setError(t("showcasePage.newVideo.notVideoFile"));
      return;
    }
    setError(null);
    setFile(f);
    if (!title) setTitle(titleFromFile(f.name));
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("showcasePage.newVideo.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              pick(e.dataTransfer.files[0]);
            }}
            onClick={() => input.current?.click()}
            sx={{
              p: 4,
              border: "2px dashed",
              borderColor: drag ? "primary.main" : "divider",
              borderRadius: 3,
              textAlign: "center",
              cursor: "pointer",
              bgcolor: drag ? "action.hover" : "transparent",
              transition: "all .2s",
            }}
          >
            <MovieIcon sx={{ fontSize: 40, color: "text.secondary" }} />
            <Typography sx={{ mt: 1 }} fontWeight="fontWeightSemibold">
              {file ? file.name : t("showcasePage.newVideo.dropHere")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {file
                ? t("showcasePage.newVideo.sizeMb", {
                    size: (file.size / 1024 / 1024).toFixed(0),
                  })
                : t("showcasePage.newVideo.originalFile")}
            </Typography>
            <input
              ref={input}
              type="file"
              accept="video/*"
              hidden
              onChange={(e) => pick(e.target.files?.[0])}
            />
          </Box>
          <TextField
            label={t("common:labels.title")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 140 }}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!file || !title.trim() || busy}
          onClick={async () => {
            if (!file) return;
            setBusy(true);
            try {
              const v = await showcaseApi.create(title.trim());
              onCreated(v, file);
            } catch (e) {
              setError(errorText(e, t));
              setBusy(false);
            }
          }}
        >
          {t("common:buttons.upload")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ShowcasePage() {
  const { t } = useTranslation(["users", "common"]);
  const navigate = useNavigate();
  const uploads = useUploads();
  const [videos, setVideos] = useState<ShowcaseVideo[] | null>(null);
  const [config, setConfig] = useState<ShowcaseConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    showcaseApi
      .list()
      .then((r) => setVideos(r.videos))
      .catch((e) => setError(errorText(e, t)));
  }, [t]);

  useEffect(() => {
    load();
    showcaseApi
      .config()
      .then(setConfig)
      .catch(() => {});
  }, [load]);

  // Keep processing rows fresh without a manual reload.
  useEffect(() => {
    const busy =
      videos?.some((v) => v.status === "transcoding") || uploads.size > 0;
    if (!busy) return;
    const t = window.setInterval(load, 8000);
    return () => window.clearInterval(t);
  }, [videos, uploads.size, load]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("showcasePage.eyebrow")}
        title={t("showcasePage.title")}
        subtitle={t("showcasePage.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            disabled={config ? !config.configured : false}
          >
            {t("showcasePage.newVideo.title")}
          </Button>
        }
      />

      {config && !config.configured && (
        <Alert severity="warning">{config.blocker}</Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!videos && !error && <LinearProgress />}

      {videos && videos.length === 0 && (
        <Card variant="outlined" sx={{ p: 6, textAlign: "center" }}>
          <MovieIcon sx={{ fontSize: 48, color: "text.secondary" }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            {t("showcasePage.noVideos")}
          </Typography>
          <Typography color="text.secondary">
            {t("showcasePage.uploadHint")}
          </Typography>
        </Card>
      )}

      {videos && videos.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          }}
        >
          {videos.map((v) => (
            <Card
              key={v.id}
              variant="outlined"
              sx={{ borderRadius: 3, overflow: "hidden" }}
            >
              <CardActionArea onClick={() => navigate(`/video/${v.id}`)}>
                <Box
                  sx={{
                    aspectRatio: "16 / 9",
                    bgcolor: "#0b0c12",
                    backgroundImage: v.posterUrl
                      ? `url(${v.posterUrl})`
                      : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                  }}
                >
                  <Box sx={{ position: "absolute", top: 10, left: 10 }}>
                    {statusChip(t, v, uploads.get(v.id))}
                  </Box>
                  {v.durationSeconds ? (
                    <Chip
                      size="small"
                      label={formatTimecode(v.durationSeconds)}
                      sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 10,
                        bgcolor: "rgba(0,0,0,.65)",
                        color: "#fff",
                      }}
                    />
                  ) : null}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography fontWeight="fontWeightSemibold" noWrap>
                    {v.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {v.stats.sessions === 0
                      ? t("showcasePage.notWatched")
                      : t("showcasePage.viewsSummary", {
                          count: v.stats.sessions,
                          pct: v.stats.avgCompletionPct,
                        })}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}

      <NewVideoDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(v, file) => {
          setCreating(false);
          startUpload(v.id, file);
          navigate(`/video/${v.id}`);
        }}
      />
    </PageContainer>
  );
}
